import { Table, TableBody, TableCell, TableHead, TableRow, Paper, Tooltip, AlertColor } from '@mui/material';
import { Scorecard, UserMinimal } from 'src/Constants/Types';
import { colorDarkSuccess, colorDarkFailure, colorLightSuccess, colorLightFailure } from 'src/GlobalStyles/MuiTheme';

const MAX_QUIZ_TEXT_LENGTH = 20;

const QuizTable = ({ quizScoreData, sessionStudents, onNotification }: {
  quizScoreData: Scorecard[] | null,
  sessionStudents: UserMinimal[] | null,
  onNotification: (message: string, notificationType?: AlertColor) => void,
}) => {

  // Convert to format the quiz text stored in the quizScoreData.questionScorecards array.
  // Prepend a "Q#" to the front and increment the number for each question.
  const getTableFormattedQuizName = (quizQuestion: { questionText: string }, index: number) => {
    const questionNumber = `Q${index + 1}`;
    const trimmedQuestionText = quizQuestion.questionText.trim();

    if (trimmedQuestionText.length > MAX_QUIZ_TEXT_LENGTH) {
      // split into two rows, end with ellipsis
      return (
        <div style={{
          width: '90px',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          {questionNumber}: {trimmedQuestionText.substring(0, MAX_QUIZ_TEXT_LENGTH / 2)}<br />{trimmedQuestionText.substring(MAX_QUIZ_TEXT_LENGTH / 2, MAX_QUIZ_TEXT_LENGTH)}...
        </div>
      );
    } else {
      // use as is
      return (
        <div style={{
          width: '90px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          wordWrap: 'break-word'
        }}>
          {questionNumber}: {trimmedQuestionText}
        </div>
      );
    }
  }

  // Display loading message if data is not loaded
  if (!quizScoreData || quizScoreData.length <= 0) {
    return <div>Loading Quiz Data...</div>;
  }

  return (
    <Paper elevation={3} style={{ width: 'fit-content', border: '1px solid black', }}>
      <Table size="small">
        <TableHead>
          {/* Quiz Question Row */}
          <TableRow>
            <TableCell style={{ border: '1px solid black', minWidth: "120px" }}>User Name</TableCell>
            {quizScoreData[0].questionScorecards.map((quizQuestion, index) => (
              <TableCell key={`${quizScoreData[0].scenarioId}-${index}`}
                style={{ border: '1px solid black', textAlign: 'center', paddingLeft: 8, paddingRight: 8, paddingTop: 8, paddingBottom: 8 }}>
                <Tooltip title={quizQuestion.questionText} arrow>
                  <span>{getTableFormattedQuizName(quizQuestion, index)}</span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sessionStudents?.map(user => (
            <TableRow key={user.id}>
              {/* User Name */}
              <TableCell style={{ border: '1px solid black', }}>{user.username}</TableCell>
              {/* Quiz Answers */}
              {quizScoreData.some(scorecard => scorecard.userId === user.id) ? (  // Check if user has a scorecard
                quizScoreData.map((scorecard, dataIndex) => { // If so map through scorecards to find the correct one
                  if (scorecard.userId === user.id) { // Once found, map through the question scorecards to display the user's answers
                    return scorecard.questionScorecards.map((questionScorecard, questionIndex) => { // Map through question scorecards to display user answers
                      const userAnswers = questionScorecard.answerScorecards
                        .filter(answer => answer.answered)  // Filter out unanswered questions
                        .map(answer => {
                          const answerIndex = questionScorecard.answerScorecards.indexOf(answer);
                          const letterGrade = String.fromCharCode(65 + answerIndex); // Convert index to letter grade (A, B, C, etc.)
                          const isCorrect = answer.answered === answer.correct;
                          const isGraded = questionScorecard.maxScore !== 0;
                          const cellColor = isGraded ? (isCorrect ? colorLightSuccess : colorLightFailure) : 'inherit';
                          return (
                            // Display tooltip with answer text on hover
                            <Tooltip key={answerIndex} title={answer.answerText || ''} arrow>
                              <b style={{ color: cellColor }}>{letterGrade}</b>
                            </Tooltip>
                          );
                        });

                      // Determine cell bacground color based on if the question is graded and if the user answered the question correctly
                      const cellBackgroundColor = questionScorecard.maxScore !== 0
                        ? (questionScorecard.maxScore === questionScorecard.score ? colorDarkSuccess : colorDarkFailure)
                        : 'inherit';

                      return (
                        // Display user answers in a cell
                        <TableCell key={`${user.id}-${questionIndex}`} style={{ border: '1px solid black', backgroundColor: cellBackgroundColor, textAlign: 'center' }}>
                          {userAnswers.reduce<JSX.Element | null>((prev, curr) => //Use reduce to concatenate user answers into a single JSX. Starting with null to avoid displaying a comma at the beginning
                            prev === null ? <><b>{curr}</b></> : <>{prev}<span>,</span> <b>{curr}</b></>, null  // Display user answers with commas between them
                          )}
                        </TableCell>
                      );
                    });
                  }
                  return null; // If the scorecard is does not match the user continue to the next scorecard
                }).filter(Boolean) //If no scorecard is found for the user, display empty cells
              ) : (
                quizScoreData[0].questionScorecards.map((_, questionIndex) => ( // Display empty cells if user has no scorecard. Based off the amount of questions in the scorecard
                  // Display empty cell if user has no scorecard. Utilize the questionIndex to determine how many empty cells to display
                  <TableCell key={`${user.id}-${questionIndex}`} style={{ border: '1px solid black', textAlign: 'center' }}>
                    -
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default QuizTable;